import { useState, useMemo } from 'react';
import DebouncedSearchBar from '@/components/common/DebouncedSearchBar/DebouncedSearchBar';
import CustomModalOverlay from '@/components/common/Custom/CustomModalOverlay/CustomModalOverlay';
import SectionTitle from '@/components/common/SectionTitle/SectionTitle';
import PaginationArrows from '@/components/ui/PaginationArrows/PaginationArrows';
import usePagination from '@/hooks/usePagination';
import { EditIcon } from '@/svgs/svgs';
import type { Team } from '@/types';

const TEAMS_PER_PAGE = 5;

interface TeamSelectorModalProps {
  teams: Team[];
  selectedTeamId: string;
  selectedTeamName: string;
  onChange: (teamId: string) => void;
}

export default function TeamSelectorModal({
  teams,
  selectedTeamId,
  selectedTeamName,
  onChange,
}: TeamSelectorModalProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const sortedTeams = useMemo(() => {
    let result = teams;
    if (searchTerm.trim()) {
      result = result.filter((team) => {
        const teamName = String((team as any)?.team_name || '');
        return teamName.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    return [...result].sort((a, b) => {
      const aId = String(a._id || a.id || '');
      const bId = String(b._id || b.id || '');
      if (aId === selectedTeamId) return -1;
      if (bId === selectedTeamId) return 1;
      return 0;
    });
  }, [teams, searchTerm, selectedTeamId]);

  const pagination = usePagination(sortedTeams, TEAMS_PER_PAGE);

  const handleSearchChange = (value: string) => {
    if (value === searchTerm) return;
    setSearchTerm(value);
    pagination.resetPage();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSearchTerm('');
    pagination.goToFirstPage();
  };

  const handleTeamSelect = (teamId: string) => {
    if (teamId !== selectedTeamId) {
      onChange(teamId);
    }
    handleCloseModal();
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="w-full flex items-center gap-1 text-input-inactive-placeholder text-xs 2xl:text-sm p-1"
      >
        <div className="w-full flex items-center justify-between gap-1">
          <p className="truncate line-clamp-1 text-base">
            {selectedTeamName || 'Select a team'}
          </p>
          <div className="w-4">
            <EditIcon />
          </div>
        </div>
      </button>

      {isModalOpen && (
        <CustomModalOverlay
          open={isModalOpen}
          onClose={handleCloseModal}
          width="550px"
        >
          <div>
            <div className="flex w-full items-center justify-between pb-4 border-b border-modal-stroke mb-4">
              <SectionTitle
                title={'Select Team'}
                description={'Assign this intake to a team based on workload capacity, ownership, and delivery priority.'}
              />
            </div>

            <DebouncedSearchBar
              placeholder="Search for a team..."
              onSearch={handleSearchChange}
              value={searchTerm}
              className="mt-4"
            />

            <div className="flex flex-col gap-1 h-[320px] p-3 bg-modal-input border rounded-lg border-modal-stroke mt-4">
              <PaginationArrows
                hasPrevious={pagination.hasPrevious}
                hasNext={pagination.hasNext}
                onPreviousPage={pagination.handlePreviousPage}
                onNextPage={pagination.handleNextPage}
                showingFrom={pagination.showingFrom}
                showingTo={pagination.showingTo}
                totalItems={pagination.totalItems}
                itemLabel="teams"
                className="mt-2 mb-3"
              />

              <div className="w-full">
                {pagination.paginatedItems.map((team) => {
                  const teamId = String(team._id || team.id || '');
                  const teamName = String(
                    (team as any)?.team_name ||
                      (team as any)?.name ||
                      'Team'
                  );
                  const isSelected = selectedTeamId === teamId;

                  return (
                    <div
                      key={teamId}
                      onClick={() => handleTeamSelect(teamId)}
                      className={`flex items-center gap-3 justify-between w-full hover:bg-[#181818]/40 rounded-lg px-3 py-1.5 transition-all duration-300 ease-in-out cursor-pointer ${
                        isSelected ? 'bg-[#181818]' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          style={{ padding: 20 * 0.2 }}
                          className={`w-5 h-5 flex items-center justify-center gap-2 rounded-full border ${
                            isSelected
                              ? 'border-navigation-accent-1'
                              : 'border-input-inactive-placeholder'
                          }`}
                        >
                          <div
                            style={{ width: 20 / 2, height: 20 / 2 }}
                            className={`w-2.5 h-2.5 rounded-full border ${
                              isSelected
                                ? 'border-navigation-accent-1 bg-navigation-accent-1'
                                : 'border-input-inactive-placeholder'
                            }`}
                          />
                        </div>
                        <div className="py-2 border-b border-modal-stroke last:border-none text-sm">
                          {teamName}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {pagination.totalItems === 0 && (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-sm text-input-inactive-placeholder">
                    No teams found.
                  </p>
                </div>
              )}
            </div>
          </div>
        </CustomModalOverlay>
      )}
    </>
  );
}
